import { Component, DestroyRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import {
  SnapshotResponseDto,
  WorkspaceConnectSettingDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { MasterOperatorFields } from '@cloud-twin/master-operator/models/master-operator-fields.enum';
import { TabDetailPanelSettings } from '@common-modules/dependencies/navigation/tab-detail-component';
import { TabDetailPanelParameters } from '@common-modules/dependencies/navigation/tab-detail-panel-parameters';
import { DynamicLayoutComponent } from '@common-modules/dynamic-layout/dynamic-layout/dynamic-layout.component';
import { DynamicLayoutBuilderService } from '@common-modules/dynamic-layout/layouts/dynamic-layout-builder-service';
import { SimpleDynamicLayoutSettings } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings';
import { SimpleDynamicLayoutSettingsItem } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings-item';
import { DynamicLayoutSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-settings';
import { DynamicLayoutStructure } from '@common-modules/dynamic-layout/models/dynamic-layout-structure';
import { LayoutNodeTypes } from '@common-modules/dynamic-layout/models/layout-node-types';
import { DynamicLayoutService } from '@common-modules/dynamic-layout/services/dynamic-layout.service';
import { SetValueAction } from '@common-modules/dynamic-layout/state/generic/generic.actions';
import { DropdownNavigationItem } from '@common-modules/navigation/models/dropdown-navigation-item';
import { StateAreas } from '@common-modules/redux/models/state-areas';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { BasePageComponent } from '@common-modules/shared/component/base-page.component';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { IPendingChangesChecker } from '@common-modules/shared/pending-changes/models/pending-changes-checker';
import { LayerEventsService } from '@common-modules/wlm-map/components/layers-selector/services/layer-events.service';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import { Observable, map, of } from 'rxjs';
import { GRID_CONTAINER_WIDGET_COMPONENT_INSTANCE } from '../widgets/ct-map-grid-container-widget/ct-map-grid-container-widget.component';
import { MAP_WIDGET_COMPONENT_INSTANCE } from '../widgets/ct-map-widget/ct-map-widget.component';

const COMPONENT_SELECTOR = 'ct-map-page';

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './ct-map-page.component.html',
  styleUrl: './ct-map-page.component.scss',
  standalone: false,
  providers: [ReduxStateService, DynamicLayoutService, LayerEventsService],
})
export class CtMapPageComponent
  extends BasePageComponent
  implements OnInit, IPendingChangesChecker, OnDestroy
{
  @ViewChild(DynamicLayoutComponent) set layoutComponent(component: DynamicLayoutComponent) {
    if (component) {
      this._dynamicLayoutService.registerLayout([component]);
    }
  }

  dynamicLayoutSettings: DynamicLayoutSettings;
  selectedSnapshots: SnapshotResponseDto[];
  selectedInstance: string;
  readonly pageId = 'MapPage';
  readonly T_SCOPE = `${TranslateScopes.CtMap}.${COMPONENT_SELECTOR}`;

  private readonly _simpleSettings: SimpleDynamicLayoutSettings = {
    layoutKey: this.pageId,
    layoutArea: 'Main',
    scopeInstanceKeys: {
      [StateAreas.Generic]: 'shared',
    },
  };

  private readonly _stateWidgetSettings = new StateWidgetSettings({
    module: this._simpleSettings.layoutArea,
    page: this._simpleSettings.layoutKey,
    scopeInstanceKeys: this._simpleSettings.scopeInstanceKeys,
  });

  private readonly _mapWidget: SimpleDynamicLayoutSettingsItem = {
    widgetName: 'CtMapWidgetComponent',
    titleKey: `${this.T_SCOPE}.widgets.map`,
    id: 'CtMapWidgetComponent#1',
  };

  private readonly _gridContainerWidget: SimpleDynamicLayoutSettingsItem = {
    widgetName: 'CtMapGridContainerWidgetComponent',
    titleKey: `${this.T_SCOPE}.widgets.grid-container`,
    id: 'CtMapGridContainerWidgetComponent#2',
  };

  private readonly _defaultWidgets = [this._mapWidget, this._gridContainerWidget];
  private readonly _defaultStructure: DynamicLayoutStructure[] = [
    {
      type: LayoutNodeTypes.Row,
      content: [
        {
          type: LayoutNodeTypes.Column,
          content: [
            {
              type: LayoutNodeTypes.Stack,
              height: 70,
              content: [
                {
                  type: LayoutNodeTypes.Component,
                  widgetInstanceKey: MAP_WIDGET_COMPONENT_INSTANCE,
                },
              ],
            },
            {
              type: LayoutNodeTypes.Stack,
              height: 30,
              content: [
                {
                  type: LayoutNodeTypes.Component,
                  widgetInstanceKey: GRID_CONTAINER_WIDGET_COMPONENT_INSTANCE,
                },
              ],
            },
          ],
        },
      ],
    },
  ];
  private setLoading: (loading: boolean) => void;

  constructor(
    private readonly _dynamicLayoutBuilderService: DynamicLayoutBuilderService,
    private readonly _dynamicLayoutService: DynamicLayoutService,
    private readonly _state: ReduxStateService,
    private readonly _spinnerService: SpinnerService,
    private readonly _selectedWorkspaceService: SelectedWorkspaceService,
    private readonly _destroyRef: DestroyRef,
  ) {
    super();

    this._state.configure(this._stateWidgetSettings);
    this.hasRightPanel = true;
    this.setLoading = this._spinnerService.buildSetLoadingFn();
  }

  checkPendingChanges(key: string): Observable<unknown> {
    return of(null);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initialLoad();
    this.bindSelectedWorkspace();
    this.setTabSettings();
  }

  mapInitParameters(parameters: TabDetailPanelParameters) {}
  init(): void {}

  private setTabSettings(): void {
    const settings = new TabDetailPanelSettings({ isDashboardContainer: true });
    this.rightPanelService.setTabSettings(settings);
  }

  /**
   * Connects the new workspace service with the existing redux action.
   * Gives priority to selectedTemporaryWorkspace$ if not null.
   */
  private bindSelectedWorkspace(): void {
    this._selectedWorkspaceService.selectedWorkspace$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((selectedWorkspace) => {
        this.dispatchConnectSettings(selectedWorkspace?.workspaceConnectSettings);
      });
  }

  private dispatchConnectSettings(connectSettings: WorkspaceConnectSettingDto[]): void {
    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.ConnectSettings,
        value: connectSettings,
      }),
    );
  }

  private initialLoad(): void {
    this.buildDynamicLayoutSettings().subscribe((settings) => {
      this.dynamicLayoutSettings = settings;
    });
  }

  private buildDynamicLayoutSettings(): Observable<DynamicLayoutSettings> {
    const simpleSettings = globalUtilsHelper.clone(this._simpleSettings, true);
    simpleSettings.items = this._defaultWidgets;

    return this._dynamicLayoutBuilderService.build(simpleSettings).pipe(
      map((dynamicLayoutSettings) => {
        dynamicLayoutSettings.showResetIcon = false;
        dynamicLayoutSettings.defaultStructure = this._defaultStructure;
        return dynamicLayoutSettings;
      }),
    );
  }

  public get titleTranslationKey(): string {
    return `${this.T_SCOPE}.title`;
  }

  public get persistencyArea(): string {
    return 'Map';
  }

  public get pageCrud(): string {
    return 'MapCrud';
  }

  public get navigations(): DropdownNavigationItem[] {
    return [];
  }
}
